import admin from "./locales/admin";
import billing from "./locales/billing";
import catalog from "./locales/catalog";
import common from "./locales/common";
import dashboard from "./locales/dashboard";
import evaluationLayers from "./locales/evaluation-layers";
import evaluationSettings from "./locales/evaluation-settings";
import evaluationVersions from "./locales/evaluation-versions";
import landing from "./locales/landing";
import lists from "./locales/lists";
import monitoring from "./locales/monitoring";
import monitoringBb from "./locales/monitoring-bb";
import monitoringFlow from "./locales/monitoring-flow";
import monitoringPods from "./locales/monitoring-pods";
import monitoringTraffic from "./locales/monitoring-traffic";
import monitoringUnevaluated from "./locales/monitoring-unevaluated";
import pizzaDetail from "./locales/pizza-detail";
import shell from "./locales/shell";
import station from "./locales/station";

/** English is the default; the stored choice of the user wins (see context/localization-context). */
export const supportedLocales = ["en", "cs"] as const;

export type Locale = (typeof supportedLocales)[number];

export const defaultLocale: Locale = "en";

export const localeLabels: Record<Locale, string> = {
  en: "English",
  cs: "Čeština",
};

/** short label of the language switch */
export const localeShortLabels: Record<Locale, string> = {
  en: "EN",
  cs: "CS",
};

/** BCP 47 tag for Intl / Date formatting */
export const intlLocale: Record<Locale, string> = {
  en: "en-US",
  cs: "cs-CZ",
};

export const dateFnsLocaleCode: Record<Locale, "cs" | "en-US"> = {
  cs: "cs",
  en: "en-US",
};

/** UI texts by area (src/i18n/locales/*.ts); every area defines English and Czech. */
const namespaces = [
  common,
  shell,
  dashboard,
  billing,
  lists,
  pizzaDetail,
  evaluationLayers,
  admin,
  landing,
  monitoring,
  monitoringPods,
  monitoringFlow,
  monitoringBb,
  monitoringTraffic,
  monitoringUnevaluated,
  evaluationSettings,
  evaluationVersions,
  catalog,
  station,
];

export const messages = {
  en: Object.assign({}, ...namespaces.map((ns) => ns.en)) as typeof common.en &
    typeof shell.en &
    typeof dashboard.en &
    typeof billing.en &
    typeof lists.en &
    typeof pizzaDetail.en &
    typeof evaluationLayers.en &
    typeof admin.en &
    typeof landing.en &
    typeof monitoring.en &
    typeof monitoringPods.en &
    typeof monitoringFlow.en &
    typeof monitoringBb.en &
    typeof monitoringTraffic.en &
    typeof monitoringUnevaluated.en &
    typeof evaluationSettings.en &
    typeof evaluationVersions.en &
    typeof catalog.en &
    typeof station.en,
  cs: Object.assign({}, ...namespaces.map((ns) => ns.cs)) as Record<string, string>,
};

export type TranslationKey = keyof typeof messages.en;

// two areas defining the same key would silently override each other
if (import.meta.env.DEV) {
  const seen = new Set<string>();
  for (const ns of namespaces) {
    for (const key of Object.keys(ns.en)) {
      if (seen.has(key)) console.warn(`i18n: the key "${key}" is defined in two areas`);
      seen.add(key);
    }
  }
}
